import React, { useRef, useState, useEffect } from 'react';
import { Button, Spinner, Badge, Modal, Dropdown } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import appUrl from '../../appUrl';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import filterFactory from 'react-bootstrap-table2-filter';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Table.css';
import { FaArrowLeft } from 'react-icons/fa6';
import { FaPlus } from 'react-icons/fa';
import { IoClose } from "react-icons/io5";

const CategoryTable = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const AppUrl = appUrl();
    const [dataLoading, setDataLoading] = useState(false);
    const [projects, setProjects] = useState([]);
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [showBackBtn, setShowBackBtn] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalContent, setModalContent] = useState(null);
    const [formData, setFormData] = useState({
        edit: { status: "", image: "", input: "" },
        new: { status: "", image: "", input: "" }
    });
    const [refreshData, setRefreshData] = useState(false);

    const fetchCategories = async (url) => {
        setDataLoading(true);
        const token = localStorage.getItem("token");
        try {
            const response = await fetch(url, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                }
            });
            const data = await response.json();
            if (data.status) {
                setProjects(data.data);
                setTotalCount(data.data.length);
            } else {
                toast.error("Failed to fetch data");
            }
        } catch (error) {
            toast.error("Error fetching reports");
        } finally {
            setDataLoading(false);
        }
    };
    
    useEffect(() => {
        if (id) {
            fetchCategories(`${AppUrl}/service/subcategories/all?category_id=${id}`);
            setShowBackBtn(true);
        } else {
            fetchCategories(`${AppUrl}/service/categories/all`);
        }
    }, [id, AppUrl, refreshData]);

    const handlePageChange = (page, sizePerPage) => {
        setCurrentPage(page);
        setPageSize(sizePerPage);
    };

    const getBadge = (status) => {
        const variants = {
            active: 'success',
            inactive: 'danger',
            pending: 'warning'
        };
        return <Badge className='status-badge' bg={variants[status.toLowerCase()] || 'secondary'}>{status === 'active' ? 'Active' : 'Inactive'}</Badge>;
    };

    const columns = !id ? [
        {
            dataField: 'index',
            text: 'S No.',
            formatter: (cell, row, rowIndex) => pageSize * (currentPage - 1) + rowIndex + 1, headerStyle: { width: '80px' }
        },
        {
            dataField: 'icon_url',
            text: 'Icons',
            formatter: cell => cell ? <img src={cell} alt="icon" style={{ width: '30px', height: '30px' }} /> : '-'
        },
        {
            dataField: 'category_name',
            text: 'Category',
            headerStyle: { width: '180px' }
        },
        {
            dataField: 'status',
            text: 'Status',
            formatter: getBadge
        },
        {
            dataField: 'actions',
            text: 'Subcategories',
            formatter: (cell, row) => <Button variant="primary" size="sm" className='view-button' style={{ width: '70px',  padding: '6px 2px'}} onClick={() => {
                setProjects([])
                navigate(`/home/categories/${row._id}`);
            }}>View</Button>
        },
        {
            text: 'Actions',
            formatter: (cell, row) => (
                <div className='flex-center'>
                    <Button onClick={() => handleModal('edit', 'category', row)} variant="primary" size="sm" className='mx-1 edit-button-lg' style={{ width: '70px', padding: '6px 2px' }}>Edit</Button>
                    <Button onClick={() => handleModal('delete', 'category', row)} variant="danger" size="sm" className='mx-1 delete-button-lg' style={{ width: '70px', padding: '6px 2px' }}>Delete</Button>
                </div>
            )
        }
    ] : [
        {
            dataField: 'index',
            text: 'S No.',
            formatter: (cell, row, rowIndex) => pageSize * (currentPage - 1) + rowIndex + 1, headerStyle: { width: '80px' }
        },
        {
            dataField: 'subcategory_name',
            text: 'Subcategory',
            headerStyle: { width: '180px' }
        },
        {
            dataField: 'status',
            text: 'Status',
            formatter: getBadge
        },
        {
            text: 'Actions',
            formatter: (cell, row) => (
                <div className='flex-center'>
                    <Button onClick={() => handleModal('edit', 'sub_category', row)} size="sm" className='mx-1 edit-button' >Edit</Button>
                    <Button onClick={() => handleModal('delete', 'sub_category', row)} variant="danger" size="sm" className='delete-button'>Delete</Button>
                </div>
            )
        }
    ];

    const handleModal = (behaviour, type, row) => {
        if(behaviour === 'edit'){
            setFormData(prev => ({
            ...prev,
            edit: { status: row.status, 
                    input: type === 'category' ? row.category_name : row.subcategory_name, 
                    image: row.icon_url 
                }
            }));
        } else if(behaviour === 'add_item'){
            setFormData(prev => ({
                ...prev,
                new: { status: '', 
                    image: '', 
                    input: '' 
                }
            }));
        }
        setModalContent({ behaviour, type, row });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setModalContent(null);
    };

    const handleEdit = async (e, type, id) => {
        e.preventDefault();
        const isCategory = type === 'category';
        const form = formData.edit;
        const url = isCategory ? `${AppUrl}/service/editcategory?category=${id}` : `${AppUrl}/service/editsubcategory?subcategory=${id}`;
        const formDataToSend = new FormData();

        // Add the data for category or subcategory
        if (isCategory) {
            formDataToSend.append('category_name', form.input);
            if (typeof(form.image) === 'object') formDataToSend.append('icon_url', form.image);
        } else {
            formDataToSend.append('subcategory_name', form.input);
        }

        formDataToSend.append('status', form.status);

        setDataLoading(true);
        const token = localStorage.getItem("token");

        try {
            const response = await fetch(url, {
                method: 'PATCH',
                body: formDataToSend,
                headers: { Authorization: `Bearer ${token}` },
            });

            if (!response.status) throw new Error('Error updating data');
            toast.success('Data updated successfully!');
            setRefreshData(prev => !prev); // Trigger re-fetch
        } catch (error) {
            toast.error('Error updating data');
        } finally {
            setDataLoading(false);
            handleCloseModal();
        }
    };

    const handleAddNewItemBtn = async (e, type) => {
        e.preventDefault();
        const isCategory = type === 'category';
        const form = formData.new;
        const url = isCategory ? `${AppUrl}/service/categories` : `${AppUrl}/service/subcategories`;
        const formDataToSend = new FormData();

        // Add the data for category or subcategory
        if (isCategory) {
            formDataToSend.append('category_name', form.input);
            if (typeof(form.image) === 'object') formDataToSend.append('icon_url', form.image);
        } else {
            formDataToSend.append('category', id)
            formDataToSend.append('subcategory_name', form.input);
        }

        formDataToSend.append('status', form.status);

        setDataLoading(true);
        const token = localStorage.getItem("token");

        try {
            const response = await fetch(url, {
                method: 'POST',
                body: formDataToSend,
                headers: { Authorization: `Bearer ${token}` },
            });

            if (!response.status) throw new Error('Error adding data');
            toast.success('Data added successfully!');
            setRefreshData(prev => !prev); // Trigger re-fetch
        } catch (error) {
            toast.error('Error adding data');
        } finally {
            setDataLoading(false);
            handleCloseModal();
        }
    };

    const handleDelete = async (e, type, id) => {
        e.preventDefault();
        const isCategory = type === 'category';
        const url = isCategory ? `${AppUrl}/service/deletecategory?category=${id}` : `${AppUrl}/service/deletesubcategory?subcategory=${id}`;
        setDataLoading(true);
        const token = localStorage.getItem("token");

        try {
            const response = await fetch(url, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!response.status) throw new Error('Error deleting data');
            toast.success('Data deleted successfully!');
            setRefreshData(prev => !prev); // Trigger re-fetch
        } catch (error) {
            toast.error('Error deleting data');
        } finally {
            setDataLoading(false);
            handleCloseModal();
        }
    };

    return (
        <>
            <ToastContainer />
            {dataLoading ?
                <div className='loading-spinner'>
                    <Spinner animation="border" />
                </div> :
                <>
                    <div className='flex items-center'>
                        {showBackBtn && (
                            <div className='flex items-center'>
                                <div onClick={() => { navigate(-1); setShowBackBtn(false) }} className='back-button'>
                                    <FaArrowLeft />
                                </div>
                            </div>
                        )}
                        <div style={{ position: 'relative', marginLeft: 'auto' }}>
                            <Button 
                            className="add-item-modal-open-btn flex gap-1 items-center justify-center border-none"
                            onClick={() => handleModal('add_item', id ? 'sub_category': 'category', null)}>
                                <FaPlus/>
                                <span>Add new</span>
                            </Button>
                        </div>
                    </div>
                    <BootstrapTable
                        bootstrap4
                        keyField="_id"
                        data={projects}
                        columns={columns}
                        filter={filterFactory()}
                        pagination={paginationFactory({
                            page: currentPage,
                            sizePerPage: pageSize,
                            totalSize: totalCount,
                            onPageChange: handlePageChange,
                            showTotal: true,
                            sizePerPageRenderer: () => null,
                            paginationTotalRenderer: (from, to, total) => (
                                <div className="pagination-and-entries">
                                    <p className="entries">
                                        Showing {from} to {to} of {total} entries
                                    </p>
                                </div>
                            )
                        })}
                    />
                </>
            }
            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>{modalContent?.behaviour === 'edit' ? `Edit ${modalContent?.type}` : modalContent?.behaviour === 'delete' ? `Delete ${modalContent?.type}` : `Add New ${modalContent?.type}`}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modalContent?.behaviour === 'edit' && (
                        <div className='flex items-center justify-between gap-2'>
                            {modalContent.type === 'category' && <FileInput 
                                image={formData.edit.image} 
                                setImage={(image) => setFormData({ ...formData, edit: { ...formData.edit, image } })}
                            />}
                            <div className='categoryEditValuesDiv'>
                                <div className='category-div bg-light w-100 border border-secondary rounded p-2 pt-1'>
                                    <p>Category</p>
                                    <input 
                                    className='category-input'
                                    type="text" 
                                    value={formData.edit.input} 
                                    onChange={e => setFormData({ ...formData, edit: { ...formData.edit, input: e.target.value } })}
                                />
                                </div>
                                <StatusDropdown 
                                    status={formData.edit.status} 
                                    setStatus={(status) => setFormData({ ...formData, edit: { ...formData.edit, status } })}
                                />
                            </div>
                        </div>
                    )}
                    {modalContent?.behaviour === 'delete' && (
                        <div>
                            <span>Are you sure you want to delete</span>
                            <span className='font-bold mx-1'>{modalContent.row.category_name || modalContent.row.subcategory_name}</span>
                            <span>{`${modalContent.type}?`}</span>
                        </div>
                    )}
                    {modalContent?.behaviour === 'add_item' && (
                        <div className='flex items-center justify-between gap-2'>
                            {modalContent.type === 'category' && <FileInput 
                                image={formData.new.image} 
                                setImage={(image) => setFormData({ ...formData, new: { ...formData.new, image } })}
                            />}
                            <div className='categoryEditValuesDiv'>
                                
                                <div className='category-div bg-light w-100 border border-secondary rounded p-2 pt-1'>
                                    <p>Category</p>
                                    <input
                                    className='category-input'
                                    type="text" 
                                    placeholder={`Enter new ${modalContent.type}`} 
                                    value={formData.new.input} 
                                    onChange={e => setFormData({ ...formData, new: { ...formData.new, input: e.target.value } })}
                                    />
                                </div>
                                <StatusDropdown
                                    status={formData.new.status} 
                                    setStatus={(status) => setFormData({ ...formData, new: { ...formData.new, status } })}
                                />
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button className='modal-cancel-btn' variant="secondary" onClick={handleCloseModal}>
                        Cancel
                    </Button>
                    {modalContent?.behaviour === 'edit' && (
                        <Button
                            disabled={dataLoading}
                            className='modal-edit-btn'
                            onClick={(e) => handleEdit(e, modalContent.type, modalContent.row._id)}>
                            {dataLoading ? 
                            <div className='flex-center'>
                                <Spinner style={{ width: '25px', height: '25px'}} animation="border" role="status" />
                            </div>
                            : 
                            <span>Save Changes</span>}
                        </Button>
                    )}
                    {modalContent?.behaviour === 'delete' && (
                        <Button
                            disabled={dataLoading}
                            className='modal-delete-btn'
                            variant="danger" 
                            onClick={(e) => handleDelete(e, modalContent.type, modalContent.row._id)}>
                            {dataLoading ? 
                            <div className='flex-center'>
                                <Spinner style={{ width: '25px', height: '25px'}} animation="border" role="status" />
                            </div>
                            : 
                            <span>Delete</span>}
                        </Button>
                    )}
                    {modalContent?.behaviour === 'add_item' && (
                        <Button 
                        disabled={dataLoading}
                        className='modal-add-btn'
                        variant="primary" 
                        onClick={(e) => handleAddNewItemBtn(e, modalContent.type)}>
                            {dataLoading ? 
                            <div className='flex-center'>
                                <Spinner style={{ width: '25px', height: '25px'}} animation="border" role="status" />
                            </div>
                            : 
                            <span>Create</span>}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CategoryTable;


export const StatusDropdown = ({ status, setStatus }) => {
    return (
        <div className='edit-status-dropdown-div bg-light w-100 border border-secondary rounded p-2 pt-1'>
            <p>Status</p>
            <Dropdown className='edit-status-dropdown' onSelect={(status) => setStatus(status)}>
                <Dropdown.Toggle variant={status === "active" ? "success" : status === "inactive" ? "danger": 'secondary'} className='w-100'>
                    {status === "active" ? "Active" : status === 'inactive' ? "Inactive" : 'Select'}
                </Dropdown.Toggle>
                <Dropdown.Menu className='w-100 bg-light text-muted'>
                    {!status ?
                         <>
                            <Dropdown.Item eventKey="active">Active</Dropdown.Item>
                            <Dropdown.Item eventKey="inactive">Inactive</Dropdown.Item>
                        </> : 
                    status === 'inactive' ? <Dropdown.Item eventKey="active">Active</Dropdown.Item> : <Dropdown.Item eventKey="inactive">Inactive</Dropdown.Item>
                    }
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
}

export const FileInput = ({ image, setImage }) => {
    const uploadImageRef = useRef();
    const [showInput, setShowInput] = useState(false);
    const [localImageUrl, setLocalImageUrl] = useState(null);

    useEffect(() => {
        if (!image) setShowInput(true);
    }, [image]);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setImage(file);
            const imageUrl = URL.createObjectURL(file);
            setLocalImageUrl(imageUrl);
            setShowInput(false);
        }
    };

    return (
        <div className="relative flex items-center justify-around">
            <div className="categoryImageDiv">
                {showInput ? (
                    <div className="w-full h-full content-center">
                        <input
                            ref={uploadImageRef}
                            className="hidden"
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                        />
                        <div
                            className="upload-container"
                            onClick={() => uploadImageRef.current.click()}
                        >
                            <div className="upload-box">
                                <div className="faPlus-icon-div">
                                    <FaPlus className="faPlus-icon" />
                                </div>
                                <span className="upload-text">Upload Image</span>
                            </div>
                        </div>
                    </div>
                ) : (
                    <img
                        className="categoryImage"
                        src={localImageUrl || image}
                        alt="category_icon"
                    />
                )}
                {!showInput && (
                    <span
                        className="image-close-icon cursor-pointer"
                        onClick={() => setShowInput(true)}
                    >
                        <IoClose />
                    </span>
                )}
            </div>
        </div>
    );
};